import { OperationStatus } from "../models/operationStatus";
import axiosConfig from "./axiosConfig";

export interface AsyncOperationProgress {
  current: number;
  total: number;
  message: string;
  // Optional within-stage row counter. When the worker reports per-row
  // progress these are populated and the FE renders "Step X of N:
  // <message> (123/250)" plus a weighted progress bar. Workers that
  // only have step-level granularity leave them unset.
  processed?: number;
  totalInStage?: number;
  // Which item the operation is working on right now. Set by workers that fan one
  // operation across separately-rendered items: several surfaces poll the same
  // operation, so the counter alone cannot tell any one of them whether it is the
  // item running, one still queued, or one already finished.
  detail?: Record<string, unknown>;
}

// One step an agent took, appended as it happened. The APPEND-only companion to
// `progress`, which only ever holds the LATEST label: a run of minutes shows a
// single rolling line and nothing about what it has been doing. Transient - the
// operation row is TTL'd - so a completed run reads its steps back from the
// durable record on the trace endpoint instead.
export interface AsyncOperationEvent {
  // Epoch seconds (BMA operation rows use int epoch throughout).
  at: number;
  kind: "thinking" | "tool" | "tool_result";
  text: string;
}

export interface AsyncOperationStatus {
  operationKey: string;
  status: OperationStatus;
  result?: Record<string, unknown>;
  error?: string;
  progress?: AsyncOperationProgress;
  events?: AsyncOperationEvent[];
}

const getAsyncStatus = async (
  authToken: string,
  siteId: string,
  key: string
): Promise<AsyncOperationStatus> => {
  const { data } = await axiosConfig.get("/async_status", {
    params: { siteId, key },
    headers: {
      Authorization: `Bearer ${authToken}`,
    },
  });
  return data;
};

export default getAsyncStatus;
